
import React, { useState, useEffect } from 'react';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';
import { ProfessionalProfile, StudentProfile } from '../types.ts';

const ReciprocityHub = ({ user }: any) => {
  const [profProfile] = useState<ProfessionalProfile | undefined>(dbService.getProfessionalProfile(user.id));
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState<{rec: any, student: StudentProfile} | null>(null);
  
  useEffect(() => {
    if (profProfile) fetchMatches();
  }, [profProfile]);

  const fetchMatches = async () => {
    setIsLoading(true);
    try {
      const allStudents = dbService.getAllStudentProfiles();
      // Directly call the Reverse Mentorship engine
      const recs = await aiService.getReverseMentorshipRecommendations(profProfile!, allStudents);
      setRecommendations(recs);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = (studentId: string) => {
    alert(`Knowledge Exchange Protocol sent to student ${studentId}.`);
    setSelectedNode(null);
  };

  return (
    <div className="p-4 md:p-12 bg-slate-50/30 dark:bg-slate-950/30 min-h-screen transition-colors duration-500 overflow-x-hidden font-sans relative">
        <header className="mb-8 md:mb-12 relative z-10">
          <div>
            <span className="text-[9px] font-black uppercase tracking-[0.5em] mb-2 block text-purple-600 dark:text-purple-400 animate-pulse">
               Innovation Radar
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-blue-950 dark:text-white tracking-tighter leading-none">
               Blind Spot Detector.
            </h1>
            <p className="mt-4 text-slate-400 dark:text-slate-500 font-medium max-w-2xl text-base md:text-lg">
               Scanning specifically for students with <strong>emerging skills</strong> (GenAI, Web3, Trends) that complement your legacy expertise. A true knowledge exchange.
            </p>
          </div>
        </header>

        <div className="flex flex-col xl:flex-row gap-8 md:gap-12 md:h-[calc(100vh-220px)]">
            {/* Nodal Map */}
            <div className="flex-1 min-h-[400px] rounded-[3rem] md:rounded-[5rem] border shadow-inner relative overflow-hidden group transition-colors duration-700 bg-purple-50/40 dark:bg-purple-900/10 border-purple-100 dark:border-purple-900/30">
               {isLoading ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-50">
                     <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                     <p className="text-[10px] font-black uppercase tracking-[0.3em] animate-pulse text-purple-600">
                        Analyzing Tech Stack Gaps...
                     </p>
                  </div>
               ) : (
                 <div className="relative w-full h-full flex items-center justify-center">
                    {/* Central Node */}
                    <div className="z-20 w-24 h-24 md:w-40 md:h-40 rounded-[2rem] md:rounded-[3rem] flex flex-col items-center justify-center text-white shadow-3xl border-4 md:border-8 transition-colors duration-500 bg-purple-600 border-white dark:border-slate-900">
                      <p className="text-[8px] md:text-[10px] font-black uppercase tracking-widest opacity-60 mb-1">YOU</p>
                      <span className="text-2xl md:text-5xl font-black">{user.fullName[0]}</span>
                    </div>

                    {/* Pulse Rings */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                       {[150, 300, 450].map(r => <div key={r} className="absolute border border-purple-500 rounded-full animate-pulse" style={{ width: r, height: r, animationDelay: `${r/100}s` }}></div>)}
                    </div>

                    {recommendations.length === 0 ? (
                      <div className="z-10 text-center opacity-30 mt-32">
                        <p className="font-black uppercase tracking-[0.3em] text-[10px]">No unique skill gaps detected</p>
                      </div>
                    ) : recommendations.map((rec, i) => {
                      const student = dbService.getStudentProfile(rec.studentId);
                      if (!student) return null;
                      
                      const angle = (i * (360 / Math.max(recommendations.length, 1))) * (Math.PI / 180);
                      const radius = window.innerWidth < 768 ? 130 : 250;
                      const x = Math.cos(angle) * radius;
                      const y = Math.sin(angle) * radius;
                      return (
                        <div key={student.userId} className="absolute transition-all duration-1000 animate-in zoom-in" style={{ transform: `translate(calc(50% + ${x}px), calc(50% + ${y}px))` }}>
                          <button 
                             onClick={() => setSelectedNode({ rec, student })}
                             className={`relative w-16 h-16 md:w-24 md:h-24 rounded-2xl md:rounded-[2.5rem] border-4 flex flex-col items-center justify-center transition-all shadow-xl group hover:scale-110 ${
                               selectedNode?.student.userId === student.userId 
                               ? 'bg-purple-600 border-white text-white scale-110'
                               : 'bg-white dark:bg-slate-900 border-purple-100 dark:border-purple-900 text-blue-950 dark:text-white'
                             }`}
                          >
                             <span className="text-lg md:text-3xl font-black">{student.fullName[0]}</span>
                             <span className="text-[6px] md:text-[8px] font-black uppercase opacity-60 mt-1">{rec.score}% Gap</span>
                             {selectedNode?.student.userId === student.userId && <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-md bg-white text-purple-600"><i className="fas fa-lightbulb text-xs"></i></div>}
                          </button>
                        </div>
                      );
                    })}
                 </div>
               )}
            </div>

            {/* Dossier */}
            {selectedNode && (
               <div className="fixed inset-0 z-[1001] bg-blue-950/90 dark:bg-black/95 backdrop-blur-xl flex items-end md:items-center justify-center p-0 md:p-6 animate-in fade-in duration-300">
                  <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-t-[3rem] md:rounded-[4rem] shadow-4xl p-8 md:p-12 relative flex flex-col gap-6 md:gap-8 animate-in slide-in-from-bottom-10 max-h-[85vh] overflow-y-auto scrollbar-hide border-t-8 border-t-purple-500 mx-auto">
                     <button onClick={() => setSelectedNode(null)} className="sticky top-0 ml-auto md:absolute md:top-10 md:right-10 w-10 h-10 bg-slate-50 dark:bg-slate-800 text-slate-400 rounded-full flex items-center justify-center transition-all z-50 hover:bg-slate-200 dark:hover:bg-slate-700"><i className="fas fa-times"></i></button>
                     
                     <header className="flex items-center gap-6 mb-2 shrink-0">
                        <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl font-black shadow-inner bg-purple-50 dark:bg-purple-900/20 text-purple-600">
                           {selectedNode.student.fullName[0]}
                        </div>
                        <div>
                           <h3 className="text-3xl font-black text-blue-950 dark:text-white tracking-tighter leading-none">{selectedNode.student.fullName}</h3>
                           <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-1">{selectedNode.student.major} @ {selectedNode.student.school}</p>
                        </div>
                     </header>

                     {/* THE EXCHANGE CARD */}
                     <section className="bg-gradient-to-br from-purple-50 to-white dark:from-slate-800 dark:to-slate-900 p-8 rounded-[2.5rem] border border-purple-100 dark:border-purple-900/50 shadow-lg relative overflow-hidden shrink-0">
                        <div className="absolute top-0 right-0 p-6 opacity-5 text-8xl text-purple-600 pointer-events-none"><i className="fas fa-exchange-alt"></i></div>
                        
                        <div className="relative z-10">
                           <div className="flex items-center gap-3 mb-6">
                              <span className="text-[10px] font-black bg-purple-600 text-white px-3 py-1 rounded-full uppercase tracking-widest shadow-md">Proposal</span>
                              <h4 className="text-xl font-black text-blue-950 dark:text-white tracking-tight">The Knowledge Exchange</h4>
                           </div>

                           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                              <div className="p-5 bg-white dark:bg-black/20 rounded-3xl border border-purple-50 dark:border-white/5 shadow-sm">
                                 <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span> Your Blind Spot
                                 </p>
                                 <p className="text-sm font-bold text-slate-700 dark:text-slate-200 leading-snug">{selectedNode.rec.professionalGap || "Legacy Systems"}</p>
                              </div>
                              <div className="p-5 bg-purple-600 text-white rounded-3xl shadow-xl border-2 border-purple-400/50">
                                 <p className="text-[9px] font-black text-purple-200 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span> Student's Edge
                                 </p>
                                 <p className="text-sm font-bold leading-snug">{selectedNode.rec.studentEdge || "Emerging Tech"}</p>
                              </div>
                           </div>

                           <p className="text-base font-medium text-slate-600 dark:text-slate-300 leading-relaxed italic px-2">
                              "{selectedNode.rec.rationale}"
                           </p>
                        </div>
                     </section>

                     <section className="p-6 rounded-[2rem] border flex gap-4 items-start bg-purple-50/50 dark:bg-purple-900/10 border-purple-100 dark:border-purple-900/30 shrink-0">
                        <i className="fas fa-comment-dots mt-1 text-purple-500"></i>
                        <div>
                           <p className="text-[9px] font-black uppercase tracking-widest mb-1 text-purple-600">Suggested Opener</p>
                           <p className="text-sm font-bold italic text-purple-900 dark:text-purple-200">"{selectedNode.rec.talkingPoint}"</p>
                        </div>
                     </section>

                     <div className="mt-auto flex gap-4 pt-6 md:pt-8 border-t border-slate-100 dark:border-slate-800 pb-2">
                        <button onClick={() => setSelectedNode(null)} className="px-8 py-5 bg-slate-50 dark:bg-slate-800 text-slate-400 hover:text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-colors">Dismiss</button>
                        <button 
                          onClick={() => handleInvite(selectedNode.student.fullName)} 
                          className="flex-1 py-5 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl active:scale-95 flex items-center justify-center gap-3 transition-all bg-purple-600 hover:bg-purple-500"
                        >
                          <i className="fas fa-handshake"></i> Propose Exchange
                        </button>
                     </div>
                  </div>
               </div>
            )}
        </div>
    </div>
  );
};

export default ReciprocityHub;
